// Way surface materials (client/src/ways2.js draws the lane ribbons of
// ways.json with them) and the bore lining texture (structs2.js, through
// main.js). Procedural 512 px canvases from a fixed hash (`rnd`, never
// Math.random): the surface at your feet reads as tarmac / gravel / ballast,
// and every load paints the same pixels. The bore lining (and structs2.js's
// detail grain) is painted in the client worker pool (R8c5: poolTexture,
// texpaint.js's painters) — no canvas painted here for it.
//   major    plain asphalt (the lane lines are ways2.js's paint shader,
//            game/waysdraw.js MARKS: drawn in metres, cut where ribbons overlap)
//   minor    plain asphalt
//   path     gravel
//   ballast  track bed with sleepers (rail + funicular)
//   rail     steel rail heads
//   stairs   concrete treads (texLen 2 m per repeat)
import * as THREE from 'three';
import { atlasTexture, setAtlasBytes } from './vegmat.js';

// makeMaterials' canvases: ~1 M fillRects in all (~0.8 s on a desktop as one
// task), so the painters await `pause` (ways2.js: sched.js slice) every
// PAUSE_N rects — the same rects in the same order, the same pixels
const PAUSE_N = 4096;
async function noiseCanvasSliced(S, fn) {
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  await fn(cv.getContext('2d'), S);
  return canvasTex(cv);
}
function canvasTex(cv) {
  const tex = new THREE.CanvasTexture(cv);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = 16;      // roads are seen at grazing angles — max sharpness
  return tex;
}

const rnd = (i) => {
  let h = (i * 374761393) ^ 0x5bf03635;
  h = (h ^ (h >> 13)) * 1274126177;
  return (((h ^ (h >> 16)) >>> 0) % 1000) / 1000;
};

async function speckle(g, S, base, amp, n, px, pause) {
  for (let i = 0; i < n; i++) {
    const v = base + (rnd(i) - 0.5) * amp;
    g.fillStyle = `rgb(${v | 0},${v | 0},${(v * 0.97) | 0})`;
    g.fillRect((rnd(i + 7919) * S) | 0, (rnd(i + 104729) * S) | 0, px, px);
    if (i % PAUSE_N === PAUSE_N - 1) await pause();
  }
}

// (R8c5) a boot texture painted in the client worker pool (run = pool.js
// run; pool-veg.js tex.paint: texpaint.js TEX_PAINT[kind] on an
// OffscreenCanvas) -> {tex, ready}. tex exists at once (a material compiles
// its program with it) but holds no pixels until `ready` resolves — the
// caller draws its material only then (a failed job rejects `ready`: the
// caller's error path, nothing painted here). The bytes are put into a main
// canvas (vegmat.js atlasTexture / setAtlasBytes: uploaded as the painted
// canvas was, version 0 until they land) with canvasTex's settings: repeat,
// sRGB, trilinear, mipmaps, anisotropy 16.
export function poolTexture(run, kind, args = {}) {
  const tex = atlasTexture(THREE.SRGBColorSpace);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.anisotropy = 16;
  const ready = run('tex.paint', { kind, args }, { prio: 'near' }).then((r) => { setAtlasBytes(tex, r.px, r.w, r.h); return tex; });
  return { tex, ready };
}

// bore lining: dark, damp rock/shotcrete with faint ring-joint banding
// (texpaint.js paintBore) -> poolTexture's {tex, ready}
export function boreTexture(run) {
  return poolTexture(run, 'bore');
}

// The ribbon materials, keyed by game/waysdraw.js RENDER `paved`/`unpaved`
// names. u runs across the ribbon, v along it (texLen m per repeat). The lane
// IS the ground (WORLD conformed the terrain to it), so no material discards.
export async function makeMaterials(pause = async () => {}) {
  const major = await noiseCanvasSliced(512, async (g, S) => {
    g.fillStyle = '#3a3a3f'; g.fillRect(0, 0, S, S);
    await speckle(g, S, 60, 34, S * S / 2, 2, pause);  // coarse aggregate
    await speckle(g, S, 64, 20, S * S / 3, 1, pause);  // fine grain
  });
  const minor = await noiseCanvasSliced(512, async (g, S) => {
    g.fillStyle = '#3f3f45'; g.fillRect(0, 0, S, S);
    await speckle(g, S, 66, 30, S * S / 2, 2, pause);
    await speckle(g, S, 70, 18, S * S / 3, 1, pause);
  });
  const path = await noiseCanvasSliced(512, async (g, S) => {
    g.fillStyle = '#8a7d68'; g.fillRect(0, 0, S, S);
    for (let i = 0; i < S * S / 2; i++) {
      const v = 118 + (rnd(i) - 0.5) * 84;
      g.fillStyle = `rgb(${v | 0},${(v * 0.9) | 0},${(v * 0.72) | 0})`;
      g.fillRect((rnd(i + 7919) * S) | 0, (rnd(i + 104729) * S) | 0, 1 + (i % 3), 1 + ((i >> 2) % 3));
      if (i % PAUSE_N === PAUSE_N - 1) await pause();
    }
  });
  const ballast = await noiseCanvasSliced(512, async (g, S) => {
    g.fillStyle = '#6d6a64'; g.fillRect(0, 0, S, S);
    await speckle(g, S, 105, 72, S * S / 2, 3, pause);
    g.fillStyle = '#4a3b2c';                       // sleepers across, v-repeat = 4 m
    for (let y = 0; y < S; y += 86) g.fillRect(56, y, S - 112, 28);
  });
  // concrete stair treads: tread banding driven by the texture V coordinate
  // at texLen 2.0 m per repeat (~6 treads/repeat at 0.3 m depth) — the
  // banding reads as stair nosings from above, no per-step geometry
  const stairs = await noiseCanvasSliced(512, async (g, S) => {
    g.fillStyle = '#aba69e'; g.fillRect(0, 0, S, S);  // warm concrete base
    await speckle(g, S, 150, 50, S * S / 3, 2, pause);  // aggregate texture
    // tread nosing lines: 2.0 m / 0.3 m ≈ 6.7 treads, each ≈ 76 px at 512px
    g.fillStyle = '#7d7974';
    const stepPx = Math.round(S / 6.7);
    for (let y = 0; y < S; y += stepPx) {
      g.fillRect(0, y, S, Math.max(2, Math.round(stepPx * 0.08)));
    }
  });
  // DoubleSide: a strip's winding flips per segment where its perpendicular
  // reverses (hairpins), so FrontSide would cull road surface seen from above
  const opts = { fog: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    side: THREE.DoubleSide };
  // creation order is draw order among equal-renderOrder opaque meshes
  // (three sorts them by material id): keep it
  return {
    major: new THREE.MeshBasicMaterial({ map: major, ...opts }),
    minor: new THREE.MeshBasicMaterial({ map: minor, ...opts }),
    path: new THREE.MeshBasicMaterial({ map: path, ...opts }),
    ballast: new THREE.MeshBasicMaterial({ map: ballast, ...opts }),
    rail: new THREE.MeshLambertMaterial({ color: 0x777d84, fog: true }),
    stairs: new THREE.MeshBasicMaterial({ map: stairs, ...opts }),
  };
}
